/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'

export type Theme = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    setTheme: (theme: Theme) => void
    actualTheme: ResolvedTheme
    isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'dhtn-theme'

/**
 * Get system theme preference
 */
const getSystemTheme = (): ResolvedTheme => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Resolve theme based on user preference and system setting
 */
const resolveTheme = (theme: Theme): ResolvedTheme => {
    if (theme === 'system') return getSystemTheme()
    return theme
}

/**
 * ThemeProvider - Manages light/dark mode across the app
 *
 * A saved preference applies before and after sign-in. Keep theme-init.js in sync
 * so navigation and session checks never flash a different surface.
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        if (typeof window === 'undefined') return 'light'
        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            return stored === 'dark' || stored === 'system' ? stored : 'light'
        } catch { return 'light' }
    })

    const resolveActual = useCallback(() => resolveTheme(theme), [theme])

    const [actualTheme, setActualTheme] = useState<ResolvedTheme>(resolveActual)

    // Follow OS changes while the preference is 'system'.
    useEffect(() => {
        setActualTheme(resolveActual())
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handleChange = () => setActualTheme(resolveActual())
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [resolveActual])

    // Apply theme class to document
    useEffect(() => {
        const root = document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(actualTheme)
        root.style.colorScheme = actualTheme
        // Correct the html background set pre-mount by theme-init.js so it can never
        // disagree with the resolved theme. Exact --dhtn-canvas tokens.
        root.style.backgroundColor = actualTheme === 'dark' ? '#0a0f1a' : '#ffffff'
        root.dataset.experience ||= 'adult'
    }, [actualTheme])

    // Persist the same preference through sign-in and sign-out.
    const handleSetTheme = useCallback((newTheme: Theme) => {
        setTheme(newTheme)
        try { localStorage.setItem(STORAGE_KEY, newTheme) } catch { /* The current tab can still change theme. */ }
    }, [])

    const value: ThemeContextType = {
        theme,
        setTheme: handleSetTheme,
        actualTheme,
        isDark: actualTheme === 'dark',
    }

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
    const ctx = useContext(ThemeContext)
    if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
    return ctx
}
